import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Box, ButtonBase, Grid, ListSubheader, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import Image from '../../Image';
// hooks
import useLocales from '../../../hooks/useLocales';

import { useDispatch, useSelector } from '../../../redux/store';
import { openLoginSignup } from '../../../redux/slices/authPages';
import useAuth from '../../../hooks/useAuth';

import useResponsive from '../../../hooks/useResponsive';
import Iconify from '../../Iconify';
import palette from '../../../theme/palette';
import LanguagePopover from '../../../layouts/dashboard/header/LanguagePopover';
import NotificationPopover from '../../../layouts/dashboard/header/NotificationPopover';
import { openConfirm } from '../../../redux/slices/confirm';
import { openTransactionsDialog } from '../../../redux/slices/transactionsDialog';
import conObj from '../../../utils/connection-assistant';

// ----------------------------------------------------------------------

export const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  })
);

// ----------------------------------------------------------------------

NavSectionVertical.propTypes = {
  isCollapse: PropTypes.bool,
  navConfig: PropTypes.array,
  onCloseSidebar: PropTypes.func,
};

const TrxButton = styled(Stack)(({ theme }) => ({
  flex: 1,
  background: 'linear-gradient(rgb(20, 44, 87) 0%, rgba(0, 0, 0, 0) 100%)',
  borderRadius: 7,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  height: 34,
  minHeight: 34,
  width: '100%',
  border: '1px groove rgb(93, 88, 150)',
}));

const ButtonIcon = styled(Image)(({ theme }) => ({
  width: '32px',
  height: '32px',
  objectFit: 'contain',
}));

const ArrowBox = styled(Box)(({ theme }) => ({
  background: 'rgb(6, 28, 70)',
  borderRadius: '9999px',
  width: '1.25rem',
  height: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ArrowIcon = styled(Iconify)(({ theme }) => ({
  width: 10,
  height: 10,
  color: '#3838ff',
  transform: 'rotate(180deg)',
}));

const NavWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: '10px',
  cursor: 'pointer',
  padding: '5px 8px',
  position: 'relative',
  height: '100%',
}));

const NavImage = styled(Box)(({ theme }) => ({
  width: '25px',
  height: '25px',
  display: 'flex',
  alignItems: 'center',
}));

const NavBtnText = styled(Typography)(() => ({
  fontSize: '15px',
  fontWeight: 900,
  fontFamily: 'sans-serif',
  lineHeight: '1.5',
  // textAlign: 'center',
  textDecoration: 'none',
  color: palette.navBarVertical.menu.color,
  marginLeft: '16px',
}));

const VisitorButton = styled(ButtonBase)(({ theme }) => ({
  borderRadius: 10,
  minHeight: 38,
  position: 'relative',
  cursor: 'pointer',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: 16,
  flex: 1,
  color: '#fff',
  fontWeight: 600,
}));

const LogoutBtn = styled(Stack)(({ theme }) => ({
  background: 'rgb(8, 15, 28)',
  border: '1px solid rgb(37, 52, 73)',
  borderRadius: '.375rem',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  width: '100%',
  height: 42,
  img: {
    height: 18,
    width: 18,
  },
  p: {
    fontSize: '.75rem',
    lineHeight: '1rem',
    color: '#fff',
    fontWeight: 500,
  },
}));

export default function NavSectionVertical({
  navConfig,
  isCollapse = false,
  onCloseSidebar = () => {},
  setOpenHistory = () => {},
  ...other
}) {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { logout } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();

  const { translate } = useLocales();

  const { promotions, userProfile } = useSelector((x) => ({
    ...x.game,
    ...x.lookup,
  }));

  const { siteSettings } = useSelector((x) => x.lookup);

  const isDesktop = useResponsive('up', 'lg');

  const isAuthenticated = localStorage.getItem('accessToken');

  const [referralDepositBonusId, setReferralDepositBonusId] = useState(null);

  const [memberBank, setMemberBank] = useState(null);

  useEffect(() => {
    getMemberBankAccounts();
  }, []);

  useEffect(() => {
    const referralDepositBonusType = promotions?.data?.filter((f) => f.promotion_type === 'referral_deposit_bonus');

    if (referralDepositBonusType?.length > 0) {
      setReferralDepositBonusId(referralDepositBonusType[0]?.id);
    }
  }, [promotions]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
      window.location.reload();
      // if (isMountedRef.current) {
      //   handleClose();
      // }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const getMemberBankAccounts = () => {
    conObj
      .post('members/banks.json')
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.status === 1) {
            // they should have bank here already. but just for the sake of checking
            if (res.data?.data?.data?.length > 0) {
              setMemberBank(res.data?.data?.data[0]);
            }
          }
        }
      })
      .catch((err) => {
        console.log(`Server Error: ${err.message}`);
      });
  };

  return (
    <Box {...other}>
      <Stack sx={{ pb: 1 }} spacing={'1rem'}>
        {isAuthenticated ? (
          <>
            <Stack direction="row" spacing={'.75rem'}>
              <Image
                src={require('../../../assets/navbar/profile.png')}
                sx={{ borderRadius: '10px', width: '30%', height: '30%' }}
              />
              <Stack>
                <Box
                  sx={{
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 100%)',
                    backgroundColor: 'rgb(255, 148, 99)',
                    p: '1px',
                    borderRadius: '10px',
                    height: '20px',
                    width: 'min-content',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      backgroundImage:
                        'radial-gradient(at center top, rgba(255, 255, 255, 0.2) 20%, rgba(0, 0, 0, 0.3) 70%, rgba(0, 0, 0, 0.25) 100%)',
                      backgroundColor: 'rgb(255, 148, 99)',
                      px: '.625rem',
                      borderRadius: '10px',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#fff' }}>
                      {userProfile?.MemberGroup?.name}
                    </Typography>
                  </Box>
                </Box>

                <Stack direction={'row'} alignItems="center">
                  <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '15px', flex: 1 }}>
                    {userProfile?.full_name}
                  </Typography>
                  <Box
                    onClick={() => navigate('/user/account')}
                    sx={{
                      cursor: 'pointer',
                      width: '18px',
                      height: '18px',
                      borderRadius: '9999px',
                      background: 'rgb(81, 72, 173)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Iconify
                      icon={'carbon:edit'}
                      sx={{
                        color: '#fff',
                        width: '12px',
                        height: '12px',
                      }}
                    />
                  </Box>
                </Stack>

                <Typography sx={{ fontWeight: 500, color: '#fff', fontSize: '13px' }}>
                  {userProfile?.UserProfile?.no_hp}
                </Typography>
                <Stack direction="row" spacing={'5px'} sx={{ alignItems: 'center' }}>
                  <Box sx={{ background: '#ddd', borderRadius: '9999px', p: '4px' }}>
                    <Image src={require('../../../assets/navbar/bank.png')} sx={{ width: '15px', height: '15px' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 500, color: '#fff', fontSize: '13px' }}>
                    {memberBank?.account_number}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack spacing={'.5rem'}>
              <Stack spacing={'.5rem'} direction="row" sx={{ flex: 1 }}>
                <TrxButton
                  direction="row"
                  onClick={() => {
                    if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
                    else dispatch(openTransactionsDialog({ open: true, isDeposit: true }));
                    onCloseSidebar();
                  }}
                >
                  <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>
                    {translate('deposit')}
                  </Typography>
                  <Image
                    src={require('../../../assets/navbar/deposit.png')}
                    sx={{ width: '15px', height: '20px', ml: '.5rem' }}
                  />
                </TrxButton>
                <TrxButton
                  direction="row"
                  onClick={() => {
                    if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
                    else dispatch(openTransactionsDialog({ open: true, isDeposit: false }));
                    onCloseSidebar();
                  }}
                >
                  <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>
                    {translate('withdraw')}
                  </Typography>
                  <Image
                    src={require('../../../assets/navbar/withdraw.png')}
                    sx={{ width: '15px', height: '20px', ml: '.5rem' }}
                  />
                </TrxButton>
              </Stack>
              <TrxButton
                direction="row"
                spacing={'.5rem'}
                sx={{ px: '.5rem' }}
                onClick={() => {
                  enqueueSnackbar(`${translate('copied')}!`, { variant: 'success', autoHideDuration: 5000 });
                  navigator.clipboard.writeText(`${window.location.origin}?aff=${userProfile?.referral_code}`);
                }}
              >
                <Image
                  src={require('../../../assets/navbar/share.png')}
                  sx={{
                    width: '20px',
                    height: '20px',
                    img: {
                      objectFit: 'contain',
                    },
                  }}
                />
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 700,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {translate('referral_link')}
                </Typography>
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 500,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {`${window.location.origin}?aff=${userProfile?.referral_code}`}
                </Typography>
                <Iconify icon="fluent:copy-16-regular" sx={{ color: '#fff', width: '15px', height: '15px' }} />
              </TrxButton>
            </Stack>
          </>
        ) : (
          <>
            <Image src={require('../../../assets/brand-logo.png')} sx={{ width: '100px', height: '48.68px' }} />
            <Stack direction="row" spacing={'.75rem'}>
              <VisitorButton
                onClick={() => dispatch(openLoginSignup({ open: true, isLogin: false }))}
                sx={{ background: 'linear-gradient(rgb(0, 59, 169), rgb(0, 77, 219))' }}
              >
                <Image
                  src={require('../../../assets/navbar/visitor-btn.webp')}
                  sx={{ position: 'absolute', borderRadius: '10px', height: '100%', width: '100%' }}
                />
                {translate('register')}
              </VisitorButton>
              <VisitorButton
                onClick={() => dispatch(openLoginSignup({ open: true, isLogin: true }))}
                sx={{ background: 'linear-gradient(rgb(230, 34, 0), rgb(255, 0, 0))' }}
              >
                <Image
                  src={require('../../../assets/navbar/visitor-btn.webp')}
                  sx={{ position: 'absolute', borderRadius: '10px', height: '100%', width: '100%' }}
                />
                {translate('login')}
              </VisitorButton>
            </Stack>
          </>
        )}

        <Stack
          spacing={'.75rem'}
          sx={{ background: 'rgba(0, 120, 213, 0.1)', p: '.5rem', borderRadius: '.5rem', cursor: 'pointer' }}
        >
          <Stack
            direction="row"
            onClick={() => {
              navigate('/vip');
            }}
            spacing={'.5rem'}
            alignItems="center"
          >
            <ButtonIcon src={require('../../../assets/navbar/vip.png')} />
            <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700, flex: 1 }}>
              {translate('vip_level')}
            </Typography>
            <ArrowBox>
              <ArrowIcon icon={'ic:round-arrow-back'} />
            </ArrowBox>
          </Stack>

          <Stack
            direction="row"
            onClick={() => {
              navigate('/referral');
            }}
            spacing={'.5rem'}
            alignItems="center"
          >
            <ButtonIcon src={require('../../../assets/navbar/refer.png')} />
            <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700, flex: 1 }}>
              {translate('refer_a_friend')}
            </Typography>
            <ArrowBox>
              <ArrowIcon icon={'ic:round-arrow-back'} />
            </ArrowBox>
          </Stack>
        </Stack>

        <Stack spacing={'.75rem'} sx={{ background: 'rgba(0, 120, 213, 0.1)', p: '.5rem', borderRadius: '.5rem' }}>
          <Grid container spacing={'1rem'}>
            <Grid item xs={4} onClick={() => navigate('/promotion')} sx={{ cursor: 'pointer' }}>
              <Stack alignItems="center">
                <ButtonIcon src={require('../../../assets/navbar/promo.png')} />
                <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>
                  {translate('promotion')}
                </Typography>
              </Stack>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
                else setOpenHistory(true);
                // navigate('/history/transaction');
                if (!isDesktop) onCloseSidebar();
              }}
            >
              <Stack alignItems="center">
                <ButtonIcon src={require('../../../assets/navbar/history.png')} />
                <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>
                  {translate('history')}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <NotificationPopover isSideNav ButtonIcon={ButtonIcon} />
              {/* <Stack alignItems="center">
                <ButtonIcon src={require('../../../assets/navbar/notification.png')} />
                <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>
                  {translate('notifications')}
                </Typography>
              </Stack> */}
            </Grid>
            <Grid item xs={4}>
              <LanguagePopover isSideNav />
            </Grid>
          </Grid>
        </Stack>

        <LogoutBtn
          direction="row"
          spacing={'.5rem'}
          onClick={() =>
            openConfirm({
              title: translate('logout_confirmation'),
              subTitle: translate('are_you_sure_to_logout'),
              onConfirm: handleLogout,
            })
          }
        >
          <Image src={require('../../../assets/navbar/logout.png')} />
          <Typography>{translate('logout')}</Typography>
        </LogoutBtn>
      </Stack>
    </Box>
  );
}

const NavBottomBtn = ({ icon, image, title, onClick = () => {} }) => (
  <NavWrapper onClick={onClick}>
    {icon ? <Iconify icon={icon} sx={{ width: '25px', height: '25px !important' }} /> : <></>}
    {image ? (
      <NavImage>
        <Image
          src={image}
          sx={{
            mx: 'auto',
            cursor: 'pointer',
            '& img': {
              maxHeight: '25px',
            },
          }}
        />
      </NavImage>
    ) : (
      <></>
    )}
    <NavBtnText>{title}</NavBtnText>
  </NavWrapper>
);
